import { store } from '../core/store.js';
import { refs } from '../core/refs.js';
import { setDevicePreset } from './preview-presets.js';
import {
  addDivider,
  addMenuItem,
  closeContextMenu,
  containsActiveMenuTarget,
  createMenu,
  getMobileAnchorPoint,
  hasActiveMenu,
  showMenu
} from './context-menu/menu-renderer.js';
import {
  applyEditorValue,
  getHistory,
  getLangFor,
  minifyCode,
  prettifyEditorCode,
  redoEditor,
  syncHistory,
  undoEditor
} from './context-menu/editor-actions.js';
import { formatShortcut } from '../core/os.js';
import { openSearchBar, closeAllSearchBars } from './context-menu/search.js';

export { closeContextMenu } from './context-menu/menu-renderer.js';

let contextMenuServices = {
  console: null
};

function clearLogs() {
  contextMenuServices.console?.clear();
}

function setConsoleFilter(filter) {
  contextMenuServices.console?.setFilter?.(filter);
}

function addConsoleFilterItems(menu) {
  const activeFilter = contextMenuServices.console?.filter || 'all';
  [
    ['all', 'Show All'],
    ['info', 'Info'],
    ['warn', 'Warnings'],
    ['error', 'Errors'],
    ['system', 'System']
  ].forEach(([filter, label]) => {
    addMenuItem(menu, {
      className: `menu-console-filter-${filter}-btn`,
      label,
      active: activeFilter === filter,
      action: () => setConsoleFilter(filter)
    });
  });
}

function addCopyLogsItem(menu) {
  addMenuItem(menu, {
    className: 'menu-copy-logs-btn',
    label: 'Copy All Logs',
    action: () => contextMenuServices.console?.copyAllLogs?.()
  });
}

function addClearLogsItem(menu) {
  addMenuItem(menu, {
    className: 'menu-clear-logs-btn',
    label: 'Clear Logs',
    shortcut: formatShortcut('⇧⌘L', 'Ctrl+Shift+L'),
    action: clearLogs
  });
}

function openPreviewAdvanced(action) {
  import('./preview-advanced.js').then(({ openPreviewAdvanced: openAdvanced }) => {
    openAdvanced(action, { console: contextMenuServices.console });
  });
}

/**
 * Spawns and configures the context menu overlay.
 */
export function openContextMenu(x, y, textarea, wrapper, lang) {
  closeContextMenu();
  syncHistory(textarea);
  const history = getHistory(textarea);

  const menu = createMenu();
  addMenuItem(menu, {
    className: 'menu-prettify-btn',
    label: 'Prettify',
    shortcut: formatShortcut('⇧⌥F', 'Shift+Alt+F'),
    action: () => {
      if (!prettifyEditorCode(textarea, lang)) {
        contextMenuServices.console?.system(`${String(lang || 'code').toUpperCase()} code is too large to format.`);
      }
    }
  });
  addMenuItem(menu, {
    className: 'menu-minify-btn',
    label: 'Minify',
    action: () => {
      applyEditorValue(textarea, minifyCode(textarea.value, lang));
    }
  });

  addDivider(menu);
  addMenuItem(menu, {
    className: 'menu-undo-btn',
    label: 'Undo',
    shortcut: formatShortcut('⌘Z', 'Ctrl+Z'),
    disabled: history.undo.length === 0,
    action: () => undoEditor(textarea)
  });
  addMenuItem(menu, {
    className: 'menu-redo-btn',
    label: 'Redo',
    shortcut: formatShortcut('⇧⌘Z', 'Ctrl+Y'),
    disabled: history.redo.length === 0,
    action: () => redoEditor(textarea)
  });

  addDivider(menu);
  addMenuItem(menu, {
    className: 'menu-search-btn',
    label: 'Search',
    shortcut: formatShortcut('⌘F', 'Ctrl+F'),
    action: () => openSearchBar(wrapper, textarea)
  });
  addMenuItem(menu, {
    className: 'menu-copy-btn',
    label: 'Copy',
    shortcut: formatShortcut('⌘C', 'Ctrl+C'),
    action: () => {
      navigator.clipboard.writeText(textarea.value).then(() => {
        contextMenuServices.console?.system('Code copied to clipboard.');
      });
    }
  });
  addMenuItem(menu, {
    className: 'menu-select-all-btn',
    label: 'Select All',
    shortcut: formatShortcut('⌘A', 'Ctrl+A'),
    action: () => {
      textarea.focus();
      textarea.select();
    }
  });
  showMenu(menu, x, y);
}

/**
 * Spawns and configures the preview presets context menu.
 */
export function openPreviewContextMenu(x, y, panel) {
  closeContextMenu();

  const menu = createMenu();
  addMenuItem(menu, {
    className: 'menu-preset-fit-btn',
    label: 'Reset View (100% Fit)',
    action: () => setDevicePreset('fit')
  });
  addDivider(menu);
  addMenuItem(menu, {
    className: 'menu-preset-mobile-btn',
    label: 'Mobile View (375x667)',
    action: () => setDevicePreset('mobile')
  });
  addMenuItem(menu, {
    className: 'menu-preset-tablet-btn',
    label: 'Tablet View (768x1024)',
    action: () => setDevicePreset('tablet')
  });
  addMenuItem(menu, {
    className: 'menu-preset-desktop-btn',
    label: 'Desktop View (1200x800)',
    action: () => setDevicePreset('desktop')
  });
  addDivider(menu);
  addMenuItem(menu, {
    className: 'menu-advanced-preview-btn',
    label: 'Advanced',
    action: () => openPreviewAdvanced('advanced')
  });
  showMenu(menu, x, y);
}

export function openConsoleContextMenu(x, y) {
  closeContextMenu();

  const menu = createMenu();
  addConsoleFilterItems(menu);
  addDivider(menu);
  addCopyLogsItem(menu);
  addClearLogsItem(menu);
  showMenu(menu, x, y);
}

function openMobileContextMenu() {
  const activeTab = store.get('activeTab') || 'html';
  const { x, y } = getMobileAnchorPoint();

  if (activeTab === 'preview') {
    openPreviewContextMenu(x, y, refs.panelPreview);
    return;
  }

  if (activeTab === 'console') {
    openConsoleContextMenu(x, y);
    return;
  }

  const textarea = {
    html: refs.htmlEditor,
    css: refs.cssEditor,
    js: refs.jsEditor
  }[activeTab];

  if (textarea) {
    openContextMenu(x, y, textarea, textarea.closest('.editor-wrapper'), getLangFor(textarea));
    return;
  }

  openConsoleContextMenu(x, y);
}

/**
 * Configures the custom context menu listeners for all editors and preview panel.
 */
export function registerContextMenu(services = {}) {
  contextMenuServices = {
    ...contextMenuServices,
    ...services
  };

  const wrappers = document.querySelectorAll('.editor-wrapper');
  wrappers.forEach((wrapper) => {
    const textarea = wrapper.querySelector('textarea');
    if (textarea && getLangFor(textarea)) {
      getHistory(textarea);
      textarea.addEventListener('input', () => syncHistory(textarea));
    }

    wrapper.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const lang = getLangFor(textarea);
      if (!textarea || !lang) return;
      openContextMenu(e.clientX, e.clientY, textarea, wrapper, lang);
    });
  });

  const previewPanel = document.getElementById('panel-preview');
  if (previewPanel) {
    previewPanel.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      openPreviewContextMenu(e.clientX, e.clientY, previewPanel);
    });
  }

  const consolePanel = document.getElementById('panel-console');
  if (consolePanel) {
    consolePanel.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      openConsoleContextMenu(e.clientX, e.clientY);
    });
  }

  if (refs.mobileContextFab) {
    refs.mobileContextFab.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openMobileContextMenu();
    });
  }

  // Close context menu on general window clicks or Escape key
  window.addEventListener('click', (e) => {
    if (hasActiveMenu() && !containsActiveMenuTarget(e.target)) {
      closeContextMenu();
    }
  });

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeContextMenu();
      closeAllSearchBars();
    }
  });
}
